import { toTypedSchema } from '@vee-validate/zod';
import { z } from '@pkg/form-builder/zod';
import { useForm } from 'vee-validate';
import { isValidPhoneNumber } from 'libphonenumber-js';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { Cookies } from '@pkg/analytics-helper/src/constants/cookies';
import { useParseMarketing } from '@pkg/analytics-helper/src/composables/useParseMarketing';
import { isMarketingConsentApproved } from '@pkg/analytics-helper/src/utilities/isMarketingConsentApproved';
import { getCookieValue } from '@pkg/analytics-helper/src/utilities/getFacebookCampaignCookies';
import { getIso2Digit } from '@pkg/utilities/i18n';
import { toHTML } from '@portabletext/to-html';
import { MARKS } from '@pkg/config/sanity/portableEditor';
import { logger } from '@pkg/logger';
import { SalesforceFormTypes } from '@pkg/types/salesforceForm';
import { CampaignParams, type SalesforceFormValues } from './types';

const serializers = {
  marks: {
    [MARKS.PAGE_LINK]: ({ value, children }: any) => {
      return `<a href="/${value?.reference?.locale || ''}${value?.reference?.slug || ''}" class="rich-validation-text" rel="noopener noreferrer">${children}</a>`;
    },
    [MARKS.URL_LINK]: ({ value, children }: any) => {
      return `<a href="${value?.href || ''}" class="rich-validation-text" rel="noopener noreferrer" target="_blank">${children}</a>`;
    },
  },
};

function salesforceFormShape(
  blockerInputValidationMessage?: any,
  platformOptionsValidationMessage?: any,
  formType?: SalesforceFormTypes,
) {
  const { $i18n } = useNuxtApp();
  const t = $i18n.t;
  const requireValidation = z.string().min(1);
  const websiteRequired = formType === SalesforceFormTypes.DATE_LABELLING;
  const platformRequired = formType === SalesforceFormTypes.PLATFORM;

  try {
    const formShape = z.object({
      blockerInput: z.any().refine(
        (val) => {
          return val === 'true';
        },
        {
          message: blockerInputValidationMessage
            ? toHTML(blockerInputValidationMessage, {
                components: serializers,
              })
            : '',
        },
      ),
      firstName: requireValidation,
      lastName: requireValidation,
      emailAddress: requireValidation.email(),
      companyName: requireValidation,
      phoneNumber: z.string().refine(value => isValidPhoneNumber(value || ''), {
        message: t('zodI18n.validations.invalid_phone'),
      }),
    });

    if (websiteRequired) {
      return formShape.extend({
        websiteName: requireValidation,
      });
    }

    if (platformRequired) {
      return formShape.extend({
        platformProduct: z.any().refine(
          (val) => {
            return val !== 'false';
          },
          {
            message: platformOptionsValidationMessage
              ? toHTML(platformOptionsValidationMessage, {
                  components: serializers,
                })
              : '',
          },
        ),
      });
    }

    return formShape;
  } catch (error) {
    logger.debug('[useSalesforceForm] ', error);
    return z.object({
      error: z.string().default('Invalid schema'),
    });
  }
}

export function convertMarketingParamsToForm(
  document: Document,
  formData: SalesforceFormValues,
  locale: string,
  productInterest: string,
) {
  const { getCampaignCookie } = useParseMarketing();
  const countryCode = computed(() => getIso2Digit(locale as LOCALE_CODE));

  const setParamAsFormValue = (param: string) => {
    const [key, value] = param.split('=');
    const formParam = Object.keys(CampaignParams).find(paramKey => paramKey === key);
    if (formParam) {
      const paramFormTerm = CampaignParams[formParam as keyof typeof CampaignParams];
      if (paramFormTerm as keyof SalesforceFormValues) {
        formData[paramFormTerm as keyof SalesforceFormValues] = value;
      }
    }
  };

  getCampaignCookie(document)?.forEach((param: any) => setParamAsFormValue(param));

  const isNewsletterConsentApproved = formData?.optInNewsletter && formData?.optInNewsletter.length > 0;

  formData.acceptedMarketingCookies = Number(!!isMarketingConsentApproved());
  formData.facebookClickId = getCookieValue(Cookies.FACEBOOK_CLICK_ID);
  formData.optInPrivacyPolicy = Number(true);
  formData.countryCode = countryCode.value;
  formData.locale = locale;
  formData.optInNewsletter = Number(!!isNewsletterConsentApproved);
  formData.productInbound =
    productInterest === SalesforceFormTypes.PLATFORM ? formData.platformProduct : productInterest;
  delete formData.blockerInput;

  return formData;
}

export function useSalesforceForm(
  blockerInputValidationMessage?: any,
  platformOptionsValidationMessage?: any,
  formType?: SalesforceFormTypes,
) {
  const formResult = useForm({
    validationSchema: toTypedSchema(
      salesforceFormShape(blockerInputValidationMessage, platformOptionsValidationMessage, formType),
    ),
  });

  return {
    formResult,
  };
}
